import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Eye, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { fadeInUp } from '@/lib/motion';

interface ArchitecturalProduct {
  id: string;
  title: string;
  description: string;
  image: string | null;
  features: string[];
  applications: string[];
  subcategories?: { title: string; image: string }[];
}

const architecturalProducts: ArchitecturalProduct[] = [
  {
    id: 'standard-profiles',
    title: 'Standard Profiles',
    description: 'Standard cross-section aluminum profile series produced for a wide range of applications.',
    image: null,
    features: [
      'Press-formed production',
      'Stock program available',
      'Fast supply capability',
      'Wide cross-section range',
      'Various alloy options',
      'Economical solutions'
    ],
    applications: [
      'General construction applications',
      'Industrial use',
      'OEM manufacturers',
      'Project applications'
    ]
  },
  {
    id: 'architectural-detail-profiles',
    title: 'Architectural Detail Solution Profiles',
    description: 'High-precision profile solutions designed for special architectural details.',
    image: IMAGES.PROFIL_DEKO_288,
    features: [
      'Custom design capability',
      'High precision',
      'Various surface treatments',
      'Complex geometries',
      'Architect-approved solutions',
      'Project-based production'
    ],
    subcategories: [
      { title: 'Exterior Corner Ceramic Profiles', image: IMAGES.A_291 },
      { title: 'Aluminum Corner Profiles', image: IMAGES.B_289 },
      { title: 'Aluminum Floor Finishing Profiles', image: IMAGES.C_292 },
      { title: 'Aluminum Step Profiles', image: IMAGES.D_290 },
      { title: 'Aluminum Balcony Profiles', image: IMAGES.F_298 },
      { title: 'Aluminum Interior Corner Profiles', image: IMAGES.G_295 },
      { title: 'Aluminum Threshold Profiles', image: IMAGES.H_294 },
      { title: 'Aluminum Stair Step Profiles', image: IMAGES.I_296 },
      { title: 'Aluminum Shower Cabin Profiles', image: IMAGES.J_297 }
    ],
    applications: [
      'Special architectural projects',
      'Restoration works',
      'Art installations',
      'Design objects'
    ]
  },
  {
    id: 'lighting-profiles',
    title: 'Lighting Profiles',
    description: 'Aesthetic and functional LED profile systems for modern lighting solutions.',
    image: IMAGES.LED_PROFILLERI_YEPYENI_287,
    features: [
      'LED strip compatible design',
      'Light distribution optimization',
      'Easy installation system',
      'Thermal management',
      'Various size options',
      'Long-lasting structure'
    ],
    applications: [
      'Interior lighting',
      'Display lighting',
      'Decorative lighting',
      'Architectural highlighting'
    ]
  },
  {
    id: 'store-blind-profiles',
    title: 'Store Blind Profiles',
    description: 'High-performance store blind systems designed for sun control and privacy.',
    image: IMAGES.PERDE_PROFILI,
    features: [
      '4" and 5" extrusion line capacity',
      'Painted (9016) and bypass options',
      'Full sun control',
      'Flexible design options',
      'Silent operation mechanism',
      'Long-lasting structure'
    ],
    applications: [
      'Residential windows',
      'Office buildings',
      'Hotels',
      'Commercial buildings'
    ]
  },
  {
    id: 'curtain-profiles',
    title: 'Curtain Track Profiles',
    description: 'Aesthetic curtain rail and profile systems for modern interior design.',
    image: IMAGES.PERDE_PROFILLERI_281,
    features: [
      'Silent sliding mechanism',
      'Various mounting options',
      'Durable alloy structure',
      'Aesthetic appearance',
      'Easy installation',
      'Long service life'
    ],
    applications: [
      'Residential interiors',
      'Office buildings',
      'Hotel rooms',
      'Commercial spaces'
    ]
  },
  {
    id: 'furniture-profiles',
    title: 'Furniture Profiles',
    description: 'Aesthetic and durable aluminum profile solutions used in furniture production.',
    image: IMAGES.MOBILYA_PROFILI,
    features: [
      '4" and 5" extrusion line capacity',
      'Painted and anodized surface options',
      'Custom cross-section design',
      'High surface quality',
      'Wide color palette',
      'Project-based production'
    ],
    applications: [
      'Kitchen furniture',
      'Office furniture',
      'Hotel furniture',
      'Custom design furniture'
    ]
  },
  {
    id: 'shower-cabin-profiles',
    title: 'Shower Cabin Profiles',
    description: 'J-type and special cross-section, hygienic and aesthetic shower cabin frame profiles.',
    image: IMAGES.BANYO_KABINI,
    features: [
      'J-type special cross-section design',
      '4" and 5" extrusion line capacity',
      'Painted and anodized surface options',
      'Water-tight sealing',
      'Easy-to-clean surface',
      'Glass retention systems'
    ],
    applications: [
      'Residential bathrooms',
      'Hotel bathrooms',
      'SPA and wellness areas',
      'Sports facilities'
    ]
  },
  {
    id: 'screen-systems',
    title: 'Insect Screen Systems',
    description: 'Aesthetic insect screen and protection systems providing comfort and hygiene.',
    image: IMAGES.SINEKLIK_301,
    features: [
      'Micro-perforated mesh structure',
      'UV resistant materials',
      'Easy installation and removal',
      'Does not block air flow',
      'Various color options',
      'Long-lasting structure'
    ],
    applications: [
      'Residential windows',
      'Balcony doors',
      'Terrace areas',
      'Office buildings'
    ]
  },
  {
    id: 'pergola-systems',
    title: 'Pergola Systems',
    description: 'Aesthetic and functional pergola solutions for outdoor living spaces.',
    image: IMAGES.PERGOLA_1_279,
    features: [
      'Adjustable louvered roof',
      'Waterproof structure',
      'Wind resistance',
      'LED lighting integration',
      'Remote control operation',
      'Modular design'
    ],
    applications: [
      'Gardens and terraces',
      'Restaurant outdoor areas',
      'Hotel poolsides',
      'Residential outdoor spaces'
    ]
  },
  {
    id: 'bioclimatic-pergola',
    title: 'Bioclimatic Pergola',
    description: 'Smart pergola systems providing climate control.',
    image: IMAGES.BIOKLIMATIK_280,
    features: [
      'Automatic climate control',
      'Sensor-based operation',
      'Energy efficient design',
      'Smart home integration',
      'Weather adaptation',
      'Silent motor technology'
    ],
    applications: [
      'Luxury residential projects',
      'Boutique hotels',
      'Premium restaurants',
      'Private villa gardens'
    ]
  },
  {
    id: 'minimal-sliding-systems',
    title: 'Minimal Sliding Systems',
    description: 'Slim profile sliding systems produced with modern minimalist design approach.',
    image: IMAGES.MINIMAL_SURME_SISTEMLER_6_299,
    features: [
      'Ultra-thin profile design',
      'Maximum glass area',
      'Silent sliding mechanism',
      'High insulation performance',
      'Aesthetic appearance',
      'Easy operation'
    ],
    applications: [
      'Modern residences',
      'Minimalist offices',
      'Art galleries',
      'Design studios'
    ]
  },
  {
    id: 'office-partition-systems',
    title: 'Office Partition Systems',
    description: 'Modular office partition and wall systems for flexible working spaces.',
    image: IMAGES.OFFICE_PART_300,
    features: [
      'Modular system design',
      'Quick assembly and disassembly',
      'Sound insulation',
      'Glass and panel options',
      'Cable passage capability',
      'Various height options'
    ],
    applications: [
      'Office buildings',
      'Coworking spaces',
      'Bank branches',
      'Hospital partitions'
    ]
  },
  {
    id: 'advertising-profiles',
    title: 'Advertising Profiles',
    description: 'Matte anodized surface profile solutions for signage, advertising boards and visual communication systems.',
    image: IMAGES.REKLAM_ICIN,
    features: [
      'Matte anodized surface treatment',
      'High aesthetic appearance',
      'Light diffusion properties',
      'Easy print compatibility',
      'Long-lasting surface',
      'Various cross-section options'
    ],
    applications: [
      'Advertising signage',
      'Illuminated panels',
      'Store signage',
      'Wayfinding systems'
    ]
  },
  {
    id: 'trailer-profiles',
    title: 'Trailer (Dorse) Profiles',
    description: 'High-strength trailer body profiles for the transportation and logistics sector.',
    image: IMAGES.TENTELI_DORSE,
    features: [
      '7" extrusion line capacity',
      'High-strength alloy',
      'Lightweight and robust structure',
      'Corrosion resistance',
      'Long service life',
      'Custom cross-section design'
    ],
    applications: [
      'Trailer bodies',
      'Freight vehicles',
      'Cold chain vehicles',
      'Special transport equipment'
    ]
  },
  {
    id: 'marine-yacht-profiles',
    title: 'Marine (Yacht) Profiles',
    description: 'Corrosion-resistant, lightweight aluminum profile systems specially developed for marine and yacht applications.',
    image: IMAGES.YACHT_MARINE_2,
    features: [
      'Marine-grade alloy (5000 & 6000 series)',
      'Superior seawater corrosion resistance',
      'Lightweight and high-strength structure',
      'Anodized and special coating options',
      'Custom cross-section design',
      'Long service life in harsh marine conditions'
    ],
    applications: [
      'Yacht and boat hulls',
      'Deck and superstructure components',
      'Marine railings and handrails',
      'Port and marina structures'
    ]
  }
];

export default function ArchitecturalSystemsEn() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.ALUMINUM_PRODUCTS_1} 
            alt="Architectural Systems" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <Link 
              to={ROUTE_PATHS.HOME_EN} 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            
            <h1 className="text-5xl font-bold text-white mb-6">
              Architectural Systems
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Aesthetic, durable and highly insulated aluminum solutions for modern buildings. 
              Our systems combine energy efficiency and design excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {architecturalProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6 }}
              >
                {product.image ? (
                  /* Product with photo - 2-column layout */
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                  }`}>
                    <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                      <div className="relative rounded-2xl overflow-hidden group">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>
                    </div>
                    <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                      <h2 className="text-3xl font-bold text-primary mb-4">{product.title}</h2>
                      <p className="text-muted-foreground mb-6 text-lg">{product.description}</p>
                      <div className="mb-8">
                        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-primary" /> Technical Features
                        </h3>
                        <ul className="space-y-2">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                              <span className="text-muted-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {product.subcategories && (
                        <div className="mb-8">
                          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Layers className="w-5 h-5 text-primary" /> Profile Categories
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {product.subcategories.map((category, idx) => (
                              <div key={idx} className="group cursor-pointer">
                                <div className="relative rounded-lg overflow-hidden mb-2 aspect-square">
                                  <img src={category.image} alt={category.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                                </div>
                                <h4 className="text-xs font-medium text-center text-foreground leading-tight">{category.title}</h4>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mb-8">
                        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-primary" /> Applications
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {product.applications.map((app, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">{app}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Product without photo - full-width card layout */
                  <div className="border border-border rounded-2xl p-8 bg-card hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Layers className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-primary mb-2">{product.title}</h2>
                        <p className="text-muted-foreground">{product.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-primary" /> Technical Features
                        </h3>
                        <ul className="space-y-2">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                              <span className="text-muted-foreground text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-primary" /> Applications
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {product.applications.map((app, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">{app}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-primary mb-6">
              Looking for Custom Solutions for Your Project?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Our expert engineering team develops custom solutions for architectural systems. 
              Let's evaluate the technical requirements of your project together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="px-8">
                <Link to={`${ROUTE_PATHS.CONTACT_EN}#quote-form`}>
                  Request Quote
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-8">
                <Link to={ROUTE_PATHS.PRODUCTS_EN}>
                  View All Products
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}